import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VariableInput } from "@/components/flow/properties/VariableInput";
import StepShell from "./StepShell";
import OptionCards from "./inputs/OptionCards";
import MultiSelectChips from "./inputs/MultiSelectChips";
import { usePracticeAreas } from "./useBriefing";

/**
 * QuestionScreen — generic renderer for the declarative questions in
 * questions.jsx. One decision per screen: resolves the question's options
 * (static, derived or from the practice-areas catalog), shows the right input
 * for its type and persists through buildPatch on every change.
 *
 * Custom questions (juridico, triagem, kickoff…) bypass this via `Component`.
 */
export default function QuestionScreen({ question, briefing, save, onAnswered }) {
  const { data: areasData } = usePracticeAreas();
  const areaOptions = (areasData?.areas || []).map((a) => ({ id: a.id, label: a.label }));

  const options = resolveOptions(question, briefing, areaOptions);
  const value = question.getValue ? question.getValue(briefing) : null;

  function apply(nextValue) {
    save(question.buildPatch(nextValue, briefing));
  }

  return (
    <StepShell title={question.title} description={question.description}>
      {question.type === "single" && (
        <OptionCards
          options={options}
          value={value}
          columns={question.columns}
          onChange={(id) => {
            apply(id);
            onAnswered?.();
          }}
        />
      )}

      {question.type === "multi" && (
        <MultiSelectChips
          options={options}
          value={Array.isArray(value) ? value : []}
          onChange={apply}
          tone={question.tone}
          emptyMessage={question.emptyMessage}
        />
      )}

      {question.type === "text" && (
        <TextAnswer question={question} value={value} onChange={apply} />
      )}

      {question.notes && (
        <NotesField question={question} briefing={briefing} save={save} />
      )}

      {question.hint && (
        <p className="mt-4 text-xs text-muted-foreground">{question.hint}</p>
      )}
    </StepShell>
  );
}

function resolveOptions(question, briefing, areaOptions) {
  if (typeof question.options === "function") return question.options(briefing) || [];
  if (Array.isArray(question.options)) return question.options;
  switch (question.optionsSource) {
    case "areas":
      return areaOptions;
    case "areasSelected": {
      const selected = briefing.areas?.selected || [];
      return areaOptions.filter((a) => selected.includes(a.id));
    }
    case "areasDeclinable": {
      const selected = briefing.areas?.selected || [];
      return areaOptions.filter((a) => !selected.includes(a.id));
    }
    default:
      return [];
  }
}

/** Free-text answer with local state (autosave is debounced upstream). */
function TextAnswer({ question, value, onChange }) {
  const [text, setText] = useState(value || "");
  return (
    <div className="max-w-md space-y-2">
      {question.inputLabel && <Label htmlFor={`q-${question.id}`}>{question.inputLabel}</Label>}
      <Input
        id={`q-${question.id}`}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onChange(e.target.value);
        }}
        placeholder={question.placeholder}
        autoComplete="off"
      />
    </div>
  );
}

/** Optional free-notes field under the main input (e.g. sales process). */
function NotesField({ question, briefing, save }) {
  const { label, placeholder, getValue, buildPatch } = question.notes;
  const [text, setText] = useState(getValue(briefing) || "");
  return (
    <div className="mt-6 space-y-2">
      <Label>{label}</Label>
      <VariableInput
        multiline
        variables={[]}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          save(buildPatch(e.target.value, briefing));
        }}
        placeholder={placeholder}
      />
    </div>
  );
}
